"use client";
import {
  ADD_CONTENT_TO_CONTINUE_WATCHING_LIST_THRESHOLD,
  ContentTypeEnum,
  contentTypeUrlValue,
  LangEnum,
  REMOVE_CONTENT_FROM_CONTINUE_WATCHING_LIST_THRESHOLD,
  ShowFormatEnum,
  SourcePagesEnum,
  SourceWidgetsEnum,
} from "@/constants/constants";
import { useGlobalContext } from "@/context/GlobalProvider";
import { Thumbnail } from "@/types/common.types";
import {
  ContinueWatchingContentInterface,
  MappedContinueWatching,
} from "@/types/home.types";
import {
  generateURLSlug,
  generateWatchPageURL,
  getContentTypeUrlValue,
  getThumbnailsFromObject,
  updateQueryparam,
} from "@/utils/common.helpers";
import { useCallback, useEffect } from "react";

/**
 * Hook to manage the Continue Watching widget state.
 * It interacts with the global context to maintain the latest state for the widget,
 * ensuring the widget is always in sync even when navigating between pages.
 */
export const useContinueWatchingWidget = () => {
  const { continueWatchingWidgetData, setContinueWatchingWidgetData } =
    useGlobalContext();

  /**
   * Updates the continue watching data in the global context
   * only if the new data has a more recent `updatedAt` timestamp.
   * @param newContinueWatchingRes - The latest continue watching widget data to check and update.
   */
  const setContinueWatchingData = useCallback(
    (newContinueWatchingRes: MappedContinueWatching) => {
      setContinueWatchingWidgetData(newContinueWatchingRes);
    },
    [continueWatchingWidgetData?.continueWatching.length],
  );

  /**
   * Adds or updates an entry in the continue watching list based on the given parameters.
   * If the content is completed, it is removed from the list.
   * @param params - Content details and playback information.
   */
  const addOrUpdateContinueWatchingData = (params: {
    playingContentid: number;
    _id: number;
    title: string;
    thumbnail: Thumbnail;
    consumedDuration: number;
    totalDuration: number;
    type: string;
    lang: string;
    dialect: string;
    currEpisodeIndex: number;
    slug: string;
    format?: ShowFormatEnum;
  }) => {
    if (!continueWatchingWidgetData || params.consumedDuration === 0) return;

    const consumedPercentage =
      (params.consumedDuration / (params.totalDuration || 100)) * 100;

    // Remove content if it exceeds the threshold for completion
    if (
      consumedPercentage >= REMOVE_CONTENT_FROM_CONTINUE_WATCHING_LIST_THRESHOLD
    ) {
      removeContentFromContinueWatchingList(params.playingContentid);
      return;
    }

    let entryUpdated = false;
    let updatedContinueWatching = [
      ...continueWatchingWidgetData.continueWatching,
    ];
    let sameShowIdIndex = null;

    // Update existing entry if it matches the playing content ID
    // and track if another episode of the this show exists in the continue watching list, if exists store its index in sameShowIdIndex
    // (to ensure only one episode per show is displayed)
    updatedContinueWatching = updatedContinueWatching.reduce<
      ContinueWatchingContentInterface[]
    >((acc, item, index) => {
      if (
        params.playingContentid !== params._id &&
        item.showId &&
        +item.showId === params._id
      ) {
        sameShowIdIndex = index;
      }
      if (item._id === params.playingContentid) {
        entryUpdated = true;

        // Update the content and move it to the front
        const updatedItem = getUpdatedItem(
          item,
          params.consumedDuration,
          consumedPercentage,
        );

        // Add the updated item to the front of the list
        acc.unshift(updatedItem); // Adds to the front of the array
      } else {
        acc.push(item); // Add unchanged items to the rest of the list
      }

      return acc;
    }, []); // Start with an empty array
    // Add a new entry if it doesn't exist and meets the threshold for inclusion
    if (
      !entryUpdated &&
      consumedPercentage >= ADD_CONTENT_TO_CONTINUE_WATCHING_LIST_THRESHOLD
    ) {
      // Remove item at sameShowIdIndex before adding the new entry, to prevent multiple epsiodes of same show in the widget
      if (sameShowIdIndex !== null) {
        updatedContinueWatching.splice(sameShowIdIndex, 1);
      }
      updatedContinueWatching = [
        getNewItem({ ...params, consumedPercentage }),
        ...updatedContinueWatching,
      ];
    }

    // Update the global context with the modified continue watching list
    setContinueWatchingWidgetData({
      ...continueWatchingWidgetData,
      continueWatching: updatedContinueWatching,
    });
  };

  /**
   * Removes an entry from the continue watching list by its content ID.
   * @param playingContentid - The ID of the content to be removed.
   */
  const removeContentFromContinueWatchingList = (playingContentid: number) => {
    if (!continueWatchingWidgetData) return;

    const newContinueWatching =
      continueWatchingWidgetData?.continueWatching.filter(
        (item) => item._id !== playingContentid,
      );

    setContinueWatchingWidgetData({
      ...continueWatchingWidgetData,
      continueWatching: newContinueWatching,
    });
  };

  /**
   * Updates an existing continue watching item with new playback data.
   * @param item - The existing continue watching item.
   * @param consumedDuration - The new consumed duration of the content.
   * @param consumedPercentage - The new consumed percentage of the content.
   * @returns The updated item.
   */
  const getUpdatedItem = (
    item: ContinueWatchingContentInterface,
    consumedDuration: number,
    consumedPercentage: number,
  ) => {
    return {
      ...item,
      consumedDuration,
      consumedPercentage,
      contentUrl: updateQueryparam(
        item.contentUrl,
        "t",
        consumedDuration.toString(),
      ),
    };
  };

  /**
   * Creates a new content object to be added to the continue watching list.
   * @param params - Details of the content to be added.
   * @returns A new `ContinueWatchingContentInterface` object.
   */
  const getNewItem = ({
    playingContentid,
    _id,
    title,
    thumbnail,
    consumedDuration,
    consumedPercentage,
    type,
    lang,
    dialect,
    currEpisodeIndex,
    totalDuration,
    format,
  }: {
    playingContentid: number;
    _id: number;
    title: string;
    thumbnail: Thumbnail;
    consumedDuration: number;
    consumedPercentage: number;
    type: string;
    lang: string;
    dialect: string;
    currEpisodeIndex: number;
    totalDuration: number;
    slug: string;
    format?: ShowFormatEnum;
  }) => {
    const thumbnails = getThumbnailsFromObject(
      thumbnail,
      type === ContentTypeEnum.SHOW,
      "small",
      "ratio1",
    );

    const slug = generateURLSlug({
      contentName: "",
      contentId: _id,
    });
    const contentTypeUrlValue = getContentTypeUrlValue(
      type as ContentTypeEnum,
      format,
    );
    const contentUrl = generateWatchPageURL(
      lang as LangEnum,
      dialect,
      contentTypeUrlValue,
      slug,
      type === ContentTypeEnum.SHOW ? currEpisodeIndex + 1 : null,
      false,
      {
        sourceWidget: SourceWidgetsEnum.CONTINUE_WATCHING,
        sourcePage: SourcePagesEnum.HOME,
        t: consumedDuration.toString() || undefined,
      },
    );

    const content: ContinueWatchingContentInterface = {
      _id: playingContentid,
      contentUrl,
      consumedPercentage,
      consumedDuration,
      title,
      totalDuration,
      type,
      showId: _id !== playingContentid ? _id : undefined,
      slug,
      ...thumbnails,
    };

    return content;
  };

  /**
   * Clears the continue watching widget data from the global context.
   * This is typically used when switching profiles to ensure fresh data is loaded.
   */
  const clearContinueWatchingData = () => {
    setContinueWatchingWidgetData(null);
  };

  return {
    continueWatchingWidgetData,
    setContinueWatchingData,
    addOrUpdateContinueWatchingData,
    clearContinueWatchingData,
  };
};
